import React from 'react';
import type { Action, ActionAuditEvent, ActionStatus } from '../../../../api/services/action';
import StatusBadge from '../../../../components/common/StatusBadge';

export const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

export const STATUS_LABELS: Record<ActionStatus, string> = {
  pending: 'Staged',
  approved: 'Approved',
  rejected: 'Rejected',
  executed: 'Executed',
};

export const STATUS_TONES: Record<ActionStatus, React.ComponentProps<typeof StatusBadge>['tone']> =
  {
    pending: 'warning',
    approved: 'info',
    rejected: 'danger',
    executed: 'success',
  };

export type StatusFilterValue = ActionStatus | 'all';

export const STATUS_FILTERS: Array<{ value: StatusFilterValue; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: STATUS_LABELS.pending },
  { value: 'approved', label: STATUS_LABELS.approved },
  { value: 'rejected', label: STATUS_LABELS.rejected },
  { value: 'executed', label: STATUS_LABELS.executed },
];

export const FALLBACK_ACTOR = 'System';

export const formatEventLabel = (event: string) =>
  event
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const getActionActors = (action: Action) => {
  const actors = new Set<string>();

  if (action.created_by) {
    actors.add(action.created_by);
  }
  if (action.approved_by) {
    actors.add(action.approved_by);
  }
  if (action.rejected_by) {
    actors.add(action.rejected_by);
  }
  action.audit_log.forEach((event) => {
    if (event.actor) {
      actors.add(event.actor);
    }
  });

  if (actors.size === 0) {
    actors.add(FALLBACK_ACTOR);
  }

  return Array.from(actors);
};

export const buildSearchIndex = (action: Action) => {
  const parts: string[] = [
    action.id,
    action.action_type,
    action.status,
    action.reasoning ?? '',
    action.created_by ?? '',
    action.approved_by ?? '',
    action.rejected_by ?? '',
    JSON.stringify(action.payload ?? {}),
    JSON.stringify(action.metadata ?? {}),
    JSON.stringify(action.execution_result ?? {}),
  ];

  action.citations.forEach((citation) => {
    parts.push(citation.path ?? '');
    parts.push(citation.text ?? '');
  });

  action.audit_log.forEach((event) => {
    parts.push(event.actor ?? '');
    parts.push(event.event);
    parts.push(JSON.stringify(event.details ?? {}));
  });

  return parts.join(' \n').toLowerCase();
};

export const AuditLogTimeline: React.FC<{ events: ActionAuditEvent[] }> = ({ events }) => {
  if (!events.length) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-6 text-sm text-neutral-500">
        No audit events recorded for this action yet.
      </div>
    );
  }

  return (
    <ul className="relative space-y-6 border-l border-neutral-200 pl-6">
      {events.map((event, index) => (
        <li key={`${event.timestamp}-${event.event}-${index}`} className="relative">
          <span className="absolute -left-[33px] top-1.5 h-3 w-3 rounded-full bg-blue-500" />
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="text-sm font-semibold text-neutral-900">
              {formatEventLabel(event.event)}
            </div>
            <div className="text-xs text-neutral-500">{formatDateTime(event.timestamp)}</div>
          </div>
          <div className="mt-2 space-y-2 text-sm text-neutral-600">
            <div>
              Actor:{' '}
              <span className="font-medium text-neutral-800">{event.actor ?? FALLBACK_ACTOR}</span>
            </div>
            {event.details && Object.keys(event.details).length ? (
              <pre className="max-h-60 overflow-auto rounded-lg bg-neutral-100 p-3 text-xs text-neutral-700">
                {JSON.stringify(event.details, null, 2)}
              </pre>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
};

type InfoTileProps = {
  label: string;
  value: string;
};

export const InfoTile: React.FC<InfoTileProps> = ({ label, value }) => (
  <div className="rounded-lg border border-neutral-200 p-3 sm:p-4 bg-neutral-50">
    <p className="text-[10px] sm:text-xs uppercase tracking-wide text-neutral-500">{label}</p>
    <p className="text-xs sm:text-sm font-medium text-neutral-900 break-all mt-0.5">{value}</p>
  </div>
);
